import { useEffect, useMemo, useState } from "react";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, ArrowDownUp, AlertTriangle, PackageOpen, Boxes, BarChart3 } from "lucide-react";
import { StatCard } from "@/components/DashboardWidgets";
import { formatCurrency } from "@/lib/format";
import { toast } from "sonner";
import { format } from "date-fns";

const fm = (n) => `$${Number(n || 0).toFixed(2)}`;

export default function Inventory() {
  const [tab, setTab] = useState("overview");
  return (
    <AppShell>
      <div className="mb-8">
        <div className="text-xs tracking-[0.2em] uppercase text-[var(--muted-text)]">Stock</div>
        <h1 className="font-display text-4xl mt-2">Inventory</h1>
        <p className="text-sm text-[var(--muted-text)] mt-1 max-w-xl">
          Products, on-hand quantities, weighted-average cost, and movement history driving COGS.
        </p>
      </div>
      <Tabs value={tab} onValueChange={setTab} className="space-y-6">
        <TabsList className="bg-[var(--hover)]">
          <TabsTrigger value="overview" data-testid="tab-inv-overview"><BarChart3 className="w-4 h-4 mr-2" /> Overview</TabsTrigger>
          <TabsTrigger value="products" data-testid="tab-inv-products"><Boxes className="w-4 h-4 mr-2" /> Products</TabsTrigger>
          <TabsTrigger value="movements" data-testid="tab-inv-movements"><ArrowDownUp className="w-4 h-4 mr-2" /> Movements</TabsTrigger>
        </TabsList>
        <TabsContent value="overview"><InvOverviewTab onOpen={setTab} /></TabsContent>
        <TabsContent value="products"><ProductsTab /></TabsContent>
        <TabsContent value="movements"><MovementsTab /></TabsContent>
      </Tabs>
    </AppShell>
  );
}

function InvOverviewTab({ onOpen }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/inventory/summary");
        setData(data);
      } catch (err) {
        console.warn("[Inventory] summary failed:", err?.message);
      }
    })();
  }, []);
  if (!data) return <div className="text-sm text-[var(--muted-text)]">Loading…</div>;
  return (
    <div className="space-y-6" data-testid="inv-overview">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Total SKUs" value={data.counts.total_sku}
                  sublabel={`${data.totals.units_on_hand} units on hand`}
                  tone="brand" testid="inv-stat-sku" />
        <StatCard label="Stock value (cost)" value={data.totals.stock_value_at_cost} currency="USD"
                  sublabel="At weighted-avg cost"
                  testid="inv-stat-value-cost" />
        <StatCard label="Stock value (retail)" value={data.totals.stock_value_at_retail} currency="USD"
                  sublabel={`Unrealized margin ${formatCurrency(data.totals.unrealized_margin, "USD")}`}
                  testid="inv-stat-value-retail" />
        <StatCard label="Low stock" value={data.counts.low_stock}
                  sublabel="At or below reorder point"
                  tone={data.counts.low_stock > 0 ? "warn" : "default"}
                  testid="inv-stat-low" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card-flat p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs tracking-[0.2em] uppercase text-[var(--muted-text)]">Low stock alerts</div>
            <button onClick={() => onOpen("products")} data-testid="inv-view-products"
                    className="text-xs text-[var(--brand)] hover:underline">View products →</button>
          </div>
          {data.low_stock.length === 0 ? (
            <div className="text-sm text-[var(--muted-text)] py-4 text-center">
              All products are above their reorder point.
            </div>
          ) : (
            <table className="w-full text-sm">
              <tbody>
                {data.low_stock.map((p) => (
                  <tr key={p.id} data-testid={`inv-low-${p.id}`}
                      className="border-b border-[var(--divider)] last:border-none">
                    <td className="py-2">
                      <div className="font-medium">{p.name}</div>
                      <div className="text-xs text-[var(--muted-text)]">{p.sku}</div>
                    </td>
                    <td className="py-2 text-right">
                      <div className="font-mono-data font-medium text-[#D4A017]">{p.qty}</div>
                      <div className="text-xs text-[var(--muted-text)]">reorder {p.reorder_point}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="card-flat p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs tracking-[0.2em] uppercase text-[var(--muted-text)]">Top by value</div>
            <button onClick={() => onOpen("products")} data-testid="inv-view-top"
                    className="text-xs text-[var(--brand)] hover:underline">View products →</button>
          </div>
          {data.top_by_value.length === 0 ? (
            <div className="text-sm text-[var(--muted-text)] py-4 text-center">No products yet.</div>
          ) : (
            <table className="w-full text-sm">
              <tbody>
                {data.top_by_value.map((p) => (
                  <tr key={p.id} data-testid={`inv-top-${p.id}`}
                      className="border-b border-[var(--divider)] last:border-none">
                    <td className="py-2">
                      <div className="font-medium">{p.name}</div>
                      <div className="text-xs text-[var(--muted-text)]">{p.qty} × {formatCurrency(p.cost, "USD")}</div>
                    </td>
                    <td className="py-2 text-right font-mono-data font-medium">{formatCurrency(p.value_at_cost, "USD")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function ProductsTab() {
  const [rows, setRows] = useState([]);
  const [report, setReport] = useState(null);
  const [open, setOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank());
  const [move, setMove] = useState({ type: "purchase", quantity: "", unit_cost: "", memo: "" });
  function blank() { return { sku: "", name: "", description: "", unit_cost: 0, unit_price: 0, quantity_on_hand: 0, reorder_point: 0, category: "" }; }
  const load = async () => {
    const [{ data: p }, { data: r }] = await Promise.all([
      api.get("/products"),
      api.get("/reports/inventory"),
    ]);
    setRows(p); setReport(r);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    const payload = { ...form,
      unit_cost: parseFloat(form.unit_cost) || 0,
      unit_price: parseFloat(form.unit_price) || 0,
      quantity_on_hand: parseFloat(form.quantity_on_hand) || 0,
      reorder_point: parseFloat(form.reorder_point) || 0,
    };
    try {
      if (editing) await api.patch(`/products/${editing.id}`, payload);
      else await api.post("/products", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try { await api.delete(`/products/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error("Delete failed"); }
  };

  const doMove = async () => {
    const q = parseFloat(move.quantity);
    if (!q) return toast.error("Quantity required");
    const payload = { type: move.type, quantity: q, memo: move.memo };
    if (move.type === "purchase" && move.unit_cost) payload.unit_cost = parseFloat(move.unit_cost);
    try {
      const { data } = await api.post(`/products/${moveOpen.id}/movement`, payload);
      toast.success(`Stock now ${data.quantity_on_hand}${data.cogs ? ` · COGS ${fm(data.cogs)}` : ""}`);
      setMoveOpen(null); setMove({ type: "purchase", quantity: "", unit_cost: "", memo: "" }); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4">
      {report && (
        <div className="grid grid-cols-3 gap-4">
          <div className="card-flat p-4"><div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Products</div><div className="font-mono-data text-lg mt-1">{report.rows.length}</div></div>
          <div className="card-flat p-4"><div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Inventory value</div><div className="font-mono-data text-lg mt-1" data-testid="inv-total-value">{fm(report.totals.value)}</div></div>
          <div className="card-flat p-4 border-l-4 border-l-[#D5A02A]"><div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Low stock</div><div className="font-mono-data text-lg mt-1" data-testid="inv-low-stock">{report.totals.low_stock}</div></div>
        </div>
      )}
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="prod-new-btn" onClick={() => { setEditing(null); setForm(blank()); setOpen(true); }} className="rounded-full bg-[#E07A5F] hover:bg-[#c9694f] text-white h-10 px-4">
              <Plus className="w-4 h-4 mr-2" /> New product
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>{editing ? "Edit product" : "New product"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>SKU</Label><Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} /></div>
              <div><Label>Category</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></div>
              <div className="col-span-2"><Label>Name</Label><Input data-testid="prod-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><Label>Unit cost</Label><Input type="number" step="0.01" data-testid="prod-form-cost" value={form.unit_cost} onChange={(e) => setForm({ ...form, unit_cost: e.target.value })} /></div>
              <div><Label>Unit price</Label><Input type="number" step="0.01" value={form.unit_price} onChange={(e) => setForm({ ...form, unit_price: e.target.value })} /></div>
              <div><Label>Quantity on hand</Label><Input type="number" value={form.quantity_on_hand} onChange={(e) => setForm({ ...form, quantity_on_hand: e.target.value })} /></div>
              <div><Label>Reorder point</Label><Input type="number" value={form.reorder_point} onChange={(e) => setForm({ ...form, reorder_point: e.target.value })} /></div>
              <div className="col-span-2"><Label>Description</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button data-testid="prod-form-save" onClick={save} className="rounded-full bg-[#2F4F4F] hover:bg-[#213838] text-[#FDFCF8]">Save</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <div className="card-flat overflow-hidden">
        {rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-[var(--muted-text)]">No products yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[var(--hover)] text-xs uppercase tracking-wider text-[var(--muted-text)]">
              <tr><th className="text-left px-5 py-2.5">SKU</th><th className="text-left px-4 py-2.5">Name</th><th className="text-right px-4 py-2.5">On hand</th><th className="text-right px-4 py-2.5">Unit cost</th><th className="text-right px-4 py-2.5">Value</th><th className="w-40" /></tr>
            </thead>
            <tbody>
              {rows.map((p) => {
                const r = (report?.rows || []).find((x) => x.id === p.id);
                const low = r?.low_stock;
                return (
                  <tr key={p.id} data-testid={`prod-row-${p.id}`} className="border-b border-[var(--divider)] last:border-none">
                    <td className="px-5 py-3 font-mono text-xs">{p.sku || "—"}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium">{p.name}</div>
                      {p.category && <div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">{p.category}</div>}
                    </td>
                    <td className={`px-4 py-3 text-right font-mono ${low ? "text-[#D5A02A]" : ""}`}>
                      {p.quantity_on_hand}
                      {low && <AlertTriangle className="inline w-3 h-3 ml-1" />}
                    </td>
                    <td className="px-4 py-3 text-right font-mono">{fm(p.unit_cost)}</td>
                    <td className="px-4 py-3 text-right font-mono">{fm(r?.value || 0)}</td>
                    <td className="px-4 py-3 text-right">
                      <Button size="sm" variant="ghost" onClick={() => setMoveOpen(p)} data-testid={`prod-move-${p.id}`}>Adjust</Button>
                      <Button size="icon" variant="ghost" onClick={() => { setEditing(p); setForm({ ...p }); setOpen(true); }}><Pencil className="w-4 h-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => remove(p.id)}><Trash2 className="w-4 h-4 text-[#E63946]" /></Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      <Dialog open={!!moveOpen} onOpenChange={(v) => !v && setMoveOpen(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Adjust stock · {moveOpen?.name}</DialogTitle><DialogDescription>Current: {moveOpen?.quantity_on_hand} @ {fm(moveOpen?.unit_cost)}</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Type</Label>
              <Select value={move.type} onValueChange={(v) => setMove({ ...move, type: v })}>
                <SelectTrigger data-testid="move-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="purchase">Purchase (increase, weighted avg cost)</SelectItem>
                  <SelectItem value="sale">Sale (decrease, posts COGS)</SelectItem>
                  <SelectItem value="adjust">Adjust (signed)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Quantity</Label><Input type="number" data-testid="move-qty" value={move.quantity} onChange={(e) => setMove({ ...move, quantity: e.target.value })} /></div>
            {move.type === "purchase" && <div><Label>Unit cost (opt)</Label><Input type="number" step="0.01" value={move.unit_cost} onChange={(e) => setMove({ ...move, unit_cost: e.target.value })} /></div>}
            <div className="col-span-2"><Label>Memo</Label><Input value={move.memo} onChange={(e) => setMove({ ...move, memo: e.target.value })} /></div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setMoveOpen(null)}>Cancel</Button>
            <Button data-testid="move-save" onClick={doMove} className="rounded-full bg-[#2F4F4F] hover:bg-[#213838] text-[#FDFCF8]">Apply</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MovementsTab() {
  const [rows, setRows] = useState([]);
  useEffect(() => { (async () => { const { data } = await api.get("/inventory/movements"); setRows(data); })(); }, []);
  return (
    <div className="card-flat overflow-hidden">
      {rows.length === 0 ? (
        <div className="p-10 text-center text-sm text-[var(--muted-text)]">No movements yet.</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-[var(--hover)] text-xs uppercase tracking-wider text-[var(--muted-text)]">
            <tr><th className="text-left px-5 py-2.5">Date</th><th className="text-left px-4 py-2.5">Product</th><th className="px-4 py-2.5">Type</th><th className="text-right px-4 py-2.5">Qty</th><th className="text-right px-4 py-2.5">Unit cost</th><th className="text-right px-4 py-2.5">COGS</th></tr>
          </thead>
          <tbody>
            {rows.map((m) => (
              <tr key={m.id} data-testid={`move-row-${m.id}`} className="border-b border-[var(--divider)] last:border-none">
                <td className="px-5 py-2.5 font-mono text-xs">{m.date}</td>
                <td className="px-4 py-2.5">{m.product_name}</td>
                <td className="px-4 py-2.5 capitalize"><span className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">{m.type}</span></td>
                <td className="px-4 py-2.5 text-right font-mono">{m.quantity}</td>
                <td className="px-4 py-2.5 text-right font-mono">{fm(m.unit_cost)}</td>
                <td className="px-4 py-2.5 text-right font-mono text-[#B4283A]">{m.cogs > 0 ? fm(m.cogs) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
